import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle } from 'lucide-react';
import { config } from '@/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// The "Stop reply emails for this comment" link in comment reply emails
// (?t=<notify_token>). Loading the page only looks the comment up; nothing
// changes until the button is clicked, because email link scanners open
// every link in an email.
function CommentRepliesStop() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const [phase, setPhase] = useState('loading'); // loading | ready | stopped | invalid
  const [postTitle, setPostTitle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/public/comment-replies/${encodeURIComponent(token)}`)
      .then((resp) => {
        if (cancelled) return;
        setPostTitle(resp.data.post_title);
        setPhase(resp.data.notify_replies ? 'ready' : 'stopped');
      })
      .catch(() => { if (!cancelled) setPhase('invalid'); });
    return () => { cancelled = true; };
  }, [token]);

  const stop = async () => {
    setBusy(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/public/comment-replies/${encodeURIComponent(token)}/stop`);
      setPhase('stopped');
    } catch (err) {
      if (err.response?.status === 404) setPhase('invalid');
      else setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const onPost = postTitle ? <> on <strong>{postTitle}</strong></> : null;

  return (
    <div className="public-page" style={{
      backgroundImage: `url(${config.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      <div className="public-container" style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        textAlign: 'center'
      }}>
        {phase === 'loading' ? (
          <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
        ) : phase === 'invalid' ? (
          <div>
            <XCircle style={{ width: '56px', height: '56px', color: '#999', margin: '0 auto 16px' }} />
            <h1>Hmm, that link didn't work</h1>
            <p>It may be old or got cut off in your email.</p>
            <p style={{ marginTop: '16px' }}>
              Just reply to the email and I'll take care of it for you.
            </p>
          </div>
        ) : phase === 'stopped' ? (
          <div data-testid="comment-replies-stopped">
            <CheckCircle style={{ width: '56px', height: '56px', color: '#28a745', margin: '0 auto 16px' }} />
            <h1>Done</h1>
            <p>You won't get any more emails about replies to your comment{onPost}.</p>
          </div>
        ) : (
          <div>
            <h1>Stop reply emails?</h1>
            <p>You'll no longer get an email when someone replies to your comment{onPost}.</p>
            <p style={{ fontSize: '14px', color: '#666' }}>This doesn't change any other emails you get from me.</p>
            {error && <p style={{ color: '#dc3545' }}>{error}</p>}
            <button
              type="button"
              className="btn btn-primary"
              onClick={stop}
              disabled={busy}
              style={{ marginTop: '16px', minWidth: '220px', opacity: busy ? 0.7 : 1 }}
              data-testid="comment-replies-stop"
            >
              {busy ? 'Saving…' : 'Stop reply emails'}
            </button>
          </div>
        )}
        <p style={{ marginTop: '24px', fontSize: '14px' }}>
          <Link to="/" style={{ color: '#1a1a1a', fontWeight: '600' }}>Back to the OBX Beach Bum</Link>
        </p>
      </div>
    </div>
  );
}

export default CommentRepliesStop;
